import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "En attente d'envoi", sent: "Transmise", accepted: "Acceptée", rejected: "Refusée", error: "Échec (à relancer)",
};

// Onglet « Facturation électronique » : la fonction est activée depuis le panel ; ici on ne fait
// que déposer la clé API fournie par la plateforme. Une clé enregistrée n'est JAMAIS réaffichée.
export default function PaTab() {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/pa/config");
      setCfg(data);
      if (data.enabled !== false) {
        const r = await api.get("/pa/submissions");
        setItems(r.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pa/api-key", { api_key: key });
      setCfg(data);
      setKey("");
      toast.success("Clé API enregistrée. Elle ne pourra plus être consultée.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer la clé API ? Les factures aux professionnels ne partiront plus vers la plateforme.")) return;
    try { const { data } = await api.delete("/pa/api-key"); setCfg(data); toast.success("Clé supprimée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const retry = async (it) => {
    try { await api.post(`/pa/retry/${it.ticket_id}`); toast.success("Nouvel essai lancé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return <Loader2 className="animate-spin" />;
  return (
    <div className="space-y-5 max-w-3xl" data-testid="pa-tab">
      <div>
        <h2 className="text-lg font-semibold">Facturation électronique (plateforme agréée)</h2>
        <p className="text-sm text-muted-foreground">
          Les factures émises à des clients professionnels (SIRET, SIREN ou TVA intracommunautaire renseignés) sont
          envoyées automatiquement à la plateforme ; seul le PDF officiel fourni par la plateforme est ensuite imprimable.
        </p>
      </div>

      {!cfg.enabled ? (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="pa-not-enabled">
          Cette fonction n'est pas activée pour votre site. Contactez votre prestataire pour l'activer.
        </p>
      ) : (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <p className="text-sm" data-testid="pa-key-state">
            Clé API :{" "}
            {cfg.configured ? <strong className="text-emerald-600">enregistrée</strong>
              : cfg.key_unreadable ? <strong className="text-destructive">illisible : saisissez-la à nouveau</strong>
              : <strong className="text-amber-600">non renseignée (les factures ne partent pas)</strong>}
          </p>
          <div className="flex gap-2">
            <Input type="password" autoComplete="new-password" value={key} onChange={(e) => setKey(e.target.value)}
                   placeholder={cfg.configured ? "Saisir une nouvelle clé pour remplacer l'actuelle" : "Coller la clé API fournie par la plateforme"}
                   data-testid="pa-key-input" />
            <Button onClick={save} disabled={saving || key.trim().length < 8} className="gap-1.5" data-testid="pa-key-save">
              <KeyRound size={14} /> Enregistrer
            </Button>
            {cfg.configured && (
              <Button variant="outline" onClick={remove} className="gap-1.5"><Trash2 size={14} /> Supprimer</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Pour votre sécurité, une clé enregistrée ne peut plus être affichée ni relue.</p>
        </div>
      )}

      {cfg.enabled && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium">Derniers envois</h3>
            <Button size="sm" variant="outline" onClick={load} className="gap-1.5"><RefreshCw size={13} /> Actualiser</Button>
          </div>
          {items.length === 0 ? <p className="text-sm text-muted-foreground">Aucun envoi pour le moment.</p> : (
            <div className="space-y-1.5">
              {items.map((it) => (
                <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                  <span><strong>{it.numero}</strong> — {STATUS_LABELS[it.status] || it.status}{it.message ? ` (${it.message})` : ""}</span>
                  {["error", "pending", "sent"].includes(it.status) && (
                    <Button size="sm" variant="outline" onClick={() => retry(it)}>Réessayer</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
